import { useNavigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Home, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTenantLogo } from '@/hooks/useTenantLogo';

export type ErrorTone = 'warning' | 'destructive' | 'info' | 'muted';

interface ErrorPageProps {
  code: string;
  title: string;
  description: string;
  /** Small inline icon shown beside the status pill (e.g. ShieldOff for 403). */
  icon?: ReactNode;
  tone?: ErrorTone;
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Optional second secondary action shown next to "Go back". */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** If provided, shows a small details panel below the description. */
  details?: string;
}

const TONE_STYLES: Record<ErrorTone, { pillBg: string; pillFg: string }> = {
  destructive: {
    pillBg: 'bg-destructive/10',
    pillFg: 'text-destructive',
  },
  warning: {
    pillBg: 'bg-warning/10',
    pillFg: 'text-warning',
  },
  info: {
    pillBg: 'bg-primary/10',
    pillFg: 'text-primary',
  },
  muted: {
    pillBg: 'bg-muted',
    pillFg: 'text-text-body',
  },
};

export function ErrorPage({
  code,
  title,
  description,
  icon,
  tone = 'muted',
  primaryAction,
  secondaryAction,
  details,
}: ErrorPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const styles = TONE_STYLES[tone];
  const logo = useTenantLogo();

  // "Go back" should never bounce the user back to the page that errored
  // (they'd just loop). Fall back to the dashboard if there's no history.
  const goBack = () => {
    if (window.history.length > 1) {
      void navigate(-1);
    } else {
      void navigate('/admin/dashboard');
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-xl text-center">
        <div className="flex flex-col items-center">
          {/* Brand mark — the hero. No surrounding card; the logo sits
              directly on the page so it actually reads. */}
          <img
            src={logo.src}
            alt={logo.alt}
            onError={logo.onError}
            className={
              logo.isCustom
                ? 'h-20 w-auto max-w-[14rem] object-contain mb-4'
                : 'h-40 sm:h-48 object-contain mb-4'
            }
          />

          {/* Small status pill — tone color tells the user at a glance
              what kind of error this is. */}
          <span
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-semibold tracking-wider mb-3',
              styles.pillBg,
              styles.pillFg,
            )}
          >
            {icon && <span className="size-3.5 flex items-center justify-center">{icon}</span>}
            {code}
          </span>

          <h1 className="text-3xl sm:text-4xl font-bold text-text-heading mb-3">
            {title}
          </h1>
          <p className="text-text-body text-base max-w-md mb-2">{description}</p>
          {location.pathname !== '/' && (
            <p className="text-xs text-text-body/60 font-mono mt-1 mb-2 break-all">
              {location.pathname}
            </p>
          )}
          {details && (
            <div className="rounded-md border border-border bg-muted/40 text-left text-xs text-text-body p-3 mt-4 max-w-md w-full">
              <pre className="whitespace-pre-wrap break-words font-mono">
                {details}
              </pre>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button variant="outline" onClick={goBack}>
            <ArrowLeft className="size-4" /> Go back
          </Button>
          {secondaryAction && (
            <Button variant="outline" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
          {primaryAction ? (
            <Button onClick={primaryAction.onClick}>{primaryAction.label}</Button>
          ) : (
            <Button onClick={() => navigate('/admin/dashboard')}>
              <Home className="size-4" /> Go to dashboard
            </Button>
          )}
        </div>

        <p className="mt-10 text-xs text-text-body/60">
          If this keeps happening, contact your IT administrator or platform support.
        </p>
      </div>
    </div>
  );
}

export const ReloadIcon = RefreshCw;
